<template>
    <div class="container-fluid px-5 mt-4">

        <!-- Nadpis stránky -->
        <div class="d-flex align-items-center justify-content-between mb-4">
            <h1 class="credits-page__title">{{ $t('cert.certificates') }}</h1>
        </div>

        <!-- Načítání -->
        <Skeletor v-if="loading" />

        <!-- Empty state -->
        <div v-if="!loading && !filteredCerts.length" class="d-flex justify-content-center mt-5 pt-4">
            <EmptyStateComponent
                :icon="iconSpecialOk"
                :icon-style="{ width: '17px', height: '17px', filter: 'brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)' }"
                :title="$t('cert.emptyCertsTitle')"
                :text="$t('cert.emptyCertsText')"
            >
                <router-link :to="{ name: 'CertCreate' }" class="btn btn-primary app-nav__create-btn">
                    {{ $t('cert.certCreate') }} <img :src="iconPlus" alt="">
                </router-link>
            </EmptyStateComponent>
        </div>

        <!-- Obsah — certifikáty existují -->
        <template v-if="!loading && filteredCerts.length">

        <!-- Modal Add Folder -->
        <div v-if="showAddFolderModal" class="create-cert-overlay" @click.self="showAddFolderModal = false">
            <div class="create-cert-modal" style="max-width:400px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('cert.addFolder') }}</span>
                    <button class="create-cert-modal__close" @click="showAddFolderModal = false">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <label class="create-cert-label mb-1">{{ $t('file.name') }}</label>
                    <input
                        type="text"
                        class="form-control mb-4"
                        v-model="newFolderName"
                        :placeholder="$t('file.newFolder')"
                    >
                    <div class="d-flex justify-content-end">
                        <button class="btn btn-primary" @click="createFolder" :disabled="!newFolderName">
                            {{ $t('file.create') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sekce Vytvořené certifikáty -->
        <div class="dashboard-section-header mb-3 mt-5">
            <div class="d-flex align-items-center gap-2">
                <img :src="iconSpecialOk" alt="" class="dashboard-section-header__icon">
                <span class="dashboard-section-header__title">{{ $t('cert.createdCertificates') }}</span>
                <span class="count-badge ms-2">{{ totalCount }}</span>
            </div>
        </div>

        <!-- Filtry + vyhledávání -->
        <div class="certs-toolbar mb-3">
            <div class="certs-filters">
                <button
                    class="certs-filter"
                    :class="{ 'certs-filter--active': activeFilter === 'all' }"
                    @click="activeFilter = 'all'"
                >{{ $t('cert.filterAll') }}</button>
                <button
                    class="certs-filter"
                    :class="{ 'certs-filter--active': activeFilter === 'shared' }"
                    @click="activeFilter = 'shared'"
                >{{ $t('cert.filterShared') }}</button>
                <button
                    v-show="false"
                    class="certs-filter"
                    :class="{ 'certs-filter--active': activeFilter === 'pending' }"
                    @click="activeFilter = 'pending'"
                >{{ $t('cert.filterPending') }}</button>
                <button
                    v-show="false"
                    class="certs-filter"
                    :class="{ 'certs-filter--active': activeFilter === 'modified' }"
                    @click="activeFilter = 'modified'"
                >
                    <span class="certs-filter__dot"></span>
                    {{ $t('cert.filterModified') }}
                </button>
            </div>

            <!-- Search input -->
            <div v-show="false" class="certs-search">
                <img :src="iconMagnifying" alt="" class="certs-search__icon">
                <input
                    type="text"
                    class="certs-search__input"
                    v-model="searchQuery"
                    :placeholder="$t('cert.searchPlaceholder')"
                >
            </div>
        </div>

        <div class="cert-list cert-list--certs-mobile">
            <div
                class="cert-list__row"
                v-for="cert in filteredCerts"
                :key="cert.uuid"
            >
                <div class="cert-list__icon-wrap">
                    <img :src="iconFile" alt="" class="cert-list__file-icon">
                </div>

                <div class="cert-list__col">
                    <span class="cert-list__label">{{ $t('cert.certName') }}</span>
                    <span class="cert-list__value">{{ cert.name }}</span>
                </div>

                <div class="cert-list__col cert-list__col--date">
                    <span class="cert-list__label">{{ $t('cert.created') }}</span>
                    <span class="cert-list__value">{{ formatDateShort(cert.created_at) }}, {{ formatTime(cert.created_at) }}</span>
                </div>

                <div class="cert-list__action">
                    <!-- Desktop: ikona sdílení před tlačítkem -->
                    <span
                        v-if="['link', 'logged'].includes(cert.file?.sharing_settings?.mode)"
                        class="cert-list__sharing-icon d-none d-md-inline-block"
                        :style="{ maskImage: `url(${iconSharingChain})`, webkitMaskImage: `url(${iconSharingChain})` }"
                    ></span>
                    <router-link
                        class="btn cert-list__view-btn"
                        :to="{ name: 'CertDetail', params: { uuid: cert.uuid } }"
                    >
                        {{ $t('cert.viewCertificate') }}
                    </router-link>
                </div>

                <!-- Mobil: ikona sdílení na posledním řádku -->
                <span
                    v-if="['link', 'logged'].includes(cert.file?.sharing_settings?.mode)"
                    class="cert-list__sharing-icon cert-list__sharing-icon--footer d-md-none"
                    :style="{ maskImage: `url(${iconSharingChain})`, webkitMaskImage: `url(${iconSharingChain})` }"
                ></span>
            </div>
        </div>

        <!-- Stránkování -->
        <div v-if="totalPages > 1" class="certs-pagination mb-5 d-flex align-items-center justify-content-center mt-5">
            <button
                class="certs-pagination__btn"
                :disabled="currentPage <= 1"
                @click="goToPage(currentPage - 1)"
            >
                <i class="bi bi-chevron-left"></i>
            </button>

            <span class="certs-pagination__label">
                {{ $t('cert.pageOf', { current: currentPage, total: totalPages }) }}
            </span>

            <button
                class="certs-pagination__btn"
                :disabled="currentPage >= totalPages"
                @click="goToPage(currentPage + 1)"
            >
                <i class="bi bi-chevron-right"></i>
            </button>
        </div>

        </template><!-- end: certifikáty existují -->

    </div>
</template>

<script>
import http from '@/http';
import { loadingState } from '@/loading.js';
import iconFile from '/public/img/new-design/icons/file.svg';
import iconSpecialOk from '/public/img/new-design/icons/special-ok-icon.svg';
import iconMagnifying from '/public/img/new-design/icons/magnifying.svg';
import iconFolderFill from '/public/img/new-design/icons/folder-fill.svg';
import iconFolderBlue from '/public/img/new-design/icons/folder-fill-blue.svg';
import iconSharingChain from '/public/img/new-design/icons/sharing_chain.svg';
import iconPlus from '/public/img/new-design/icons/plus.svg';

export default {
    mounted() {
        if (this.$route.query.filter) {
            this.activeFilter = this.$route.query.filter;
        }
        this.getCertsList();
        this.getFolders();
        this.emitter.on('certAdded', (newCert) => {
            this.certsList.unshift(newCert);
        });
    },

    data() {
        return {
            certsList: [],
            loading: true,
            activeFilter: 'all',
            searchQuery: '',
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            folders: [],
            foldersLoading: true,
            showAddFolderModal: false,
            showAllFolders: false,
            newFolderName: '',
            iconFile,
            iconSpecialOk,
            iconMagnifying,
            iconFolderFill,
            iconFolderBlue,
            iconSharingChain,
            iconPlus,
        };
    },

    computed: {
        visibleFolders() {
            return this.showAllFolders ? this.folders : this.folders.slice(0, 4);
        },

        filteredCerts() {
            let list = this.certsList;

            // Filtrování podle záložky — zatím jen "all" je funkční
            if (this.activeFilter === 'shared') {
                list = list.filter(c => ['link', 'logged'].includes(c.file?.sharing_settings?.mode));
            } else if (this.activeFilter === 'pending') {
                list = list.filter(c => c.status === 'pending');
            } else if (this.activeFilter === 'modified') {
                list = list.filter(c => c.modified);
            }

            // Vyhledávání podle názvu
            const q = this.searchQuery.trim().toLowerCase();
            if (q) {
                list = list.filter(c => c.name?.toLowerCase().includes(q));
            }

            return list;
        },
    },

    methods: {
        getCertsList() {
            this.loading = true;
            http.get('/certs/list', { params: { page: this.currentPage } }).then((response) => {
                this.certsList = response.data.userCerts;
                this.totalPages = response.data.meta.totalPages;
                this.currentPage = response.data.meta.page;
                this.totalCount = response.data.meta.totalRecordsCount;
                this.loading = false;
                loadingState.active = false;
            });
        },

        getFolders() {
            this.foldersLoading = true;
            http.get('/files/folders').then((response) => {
                this.folders = (response.data.files || []).filter(f => f.type === 'FOLDER');
            }).catch(() => {
                this.folders = [];
            }).finally(() => {
                this.foldersLoading = false;
            });
        },

        createFolder() {
            if (!this.newFolderName) return;
            http.post('/files/folders/create', { name: this.newFolderName }).then((response) => {
                if (response.data.file) {
                    this.folders.unshift(response.data.file);
                }
                this.showAddFolderModal = false;
                this.newFolderName = '';
            });
        },

        goToPage(page) {
            if (page < 1 || page > this.totalPages) return;
            this.currentPage = page;
            this.getCertsList();
        },

        formatDateShort(dateStr) {
            const d = new Date(dateStr);
            const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
            return `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
        },

        formatTime(dateStr) {
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
    },
};
</script>